import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'environments/environment';

import { DasLexService } from 'app/services/daslex-service';
import { CommonsText } from 'app/commons/CommonsText';

export interface BenefitCard {
    titulo: string;
    descripcion: string;
    precio: string;
    descuento: string;
    iconKey: string;
    detalleSlug: string;
    categoriaSlug: string;
    externalUrl: string;
}

export interface BenefitSection {
    titulo: string;
    slug: string;
    cards: BenefitCard[];
}

@Component({
    selector: 'app-benefits-page',
    templateUrl: './benefits-page.component.html',
    styleUrls: ['./benefits-page.component.scss']
})
export class BenefitsPageComponent implements OnInit {

    loading: boolean = false;
    sections: BenefitSection[] = [];
    infoText: string = 'Disfruta de un 20 % de descuento en servicios legales por ser de onLygal.';

    private dragState = new WeakMap<HTMLElement, {
        isDown: boolean;
        startX: number;
        startScroll: number;
        moved: boolean;
    }>();

    private dragJustEnded = false;
    private readonly DRAG_THRESHOLD_PX = 4;

    constructor(
        private router: Router,
        private dasLexService: DasLexService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.load();
    }

    onScrollerMouseDown(event: MouseEvent): void {
        if (event.button !== 0) { return; }

        const el = event.currentTarget as HTMLElement;

        this.dragState.set(el, {
            isDown: true,
            startX: event.pageX,
            startScroll: el.scrollLeft,
            moved: false
        });
    }

    onScrollerMouseMove(event: MouseEvent): void {
        const el = event.currentTarget as HTMLElement;
        const s = this.dragState.get(el);

        if (!s || !s.isDown) { return; }

        const dx = event.pageX - s.startX;

        if (!s.moved && Math.abs(dx) < this.DRAG_THRESHOLD_PX) {
            return;
        }

        s.moved = true;
        el.classList.add('is-dragging');
        el.scrollLeft = s.startScroll - dx;
        event.preventDefault();
    }

    onScrollerMouseUp(event: MouseEvent): void {
        const el = event.currentTarget as HTMLElement;
        const s = this.dragState.get(el);

        if (!s) { return; }

        if (s.moved) {
            this.dragJustEnded = true;
            setTimeout(() => {
                this.dragJustEnded = false;
            }, 50);
        }

        s.isDown = false;
        s.moved = false;
        el.classList.remove('is-dragging');
    }

    irDetalle(card: BenefitCard): void {
        if (this.dragJustEnded) { return; }

        if (card.externalUrl) {
            window.open(card.externalUrl, '_blank', 'noopener');
            return;
        }
    }

    // El iconKey de cada tarjeta es la descripción del producto de Legaliboo,
    // así que el mapa busca los términos que llegan de allí además de las claves originales.
    iconForCard(card: BenefitCard): string {
        const base = 'resources/assets/images/main-page/';

        const map: { [k: string]: string } = {
            vivienda:   'house.svg',
            hogar:      'house.svg',
            certificad: 'house.svg',
            house:      'house.svg',
            vehiculo:   'car-blue.svg',
            coche:      'car-blue.svg',
            car:        'car-blue.svg',
            pension:    'shield.svg',
            jubilacion: 'shield.svg',
            shield:     'shield.svg',
            sucesion:   'calculator-outline.svg',
            herencia:   'calculator-outline.svg',
            calculator: 'calculator-outline.svg'
        };

        const key = this.normalizar(card && card.iconKey);

        for (const k of Object.keys(map)) {
            if (key && key.indexOf(k) !== -1) {
                return base + map[k];
            }
        }

        return base + 'house.svg';
    }

    private normalizar(texto: string): string {
        return (texto || '').toLowerCase().trim()
            .replace(/[áàâä]/g, 'a')
            .replace(/[éèêë]/g, 'e')
            .replace(/[íìîï]/g, 'i')
            .replace(/[óòôö]/g, 'o')
            .replace(/[úùûü]/g, 'u');
    }

    /**
     * Catálogo de Legaliboo: cada producto es una sección y cada uno de sus servicios,
     * una tarjeta. Se descartan los productos dados de baja y los que se quedan
     * sin servicios activos, para no pintar secciones vacías.
     */
    private load(): void {
        this.loading = true;

        this.dasLexService.getProducts()
            .then(success => {
                const productos =
                    (success && success.response && success.response.data)
                        ? success.response.data
                        : [];

                this.sections = productos
                    .filter(producto => producto && producto.productoActivo)
                    .map(producto => ({
                        titulo: producto.productoNombre || '',
                        slug: '',
                        cards: this.cardsDeProducto(producto)
                    } as BenefitSection))
                    .filter(section => section.cards.length > 0);

                this.loading = false;
            })
            .catch(() => {
                this.sections = [];
                this.loading = false;

                this.toastr.error(
                    CommonsText.PROBLEMA_SERVICIO_LEGALIBOO,
                    CommonsText.PROBLEMA_DE_CONECTIVIDAD,
                    {
                        timeOut: 5000
                    }
                );
            });
    }

    private cardsDeProducto(producto: any): BenefitCard[] {
        return ((producto && producto.servicios) || [])
            .filter(servicio => servicio && servicio.servicioActivo)
            .map(servicio => ({
                titulo: servicio.servicioNombre || '',
                descripcion: servicio.servicioDescripcion || '',
                // El precio no se muestra: el *ngIf de la plantilla oculta el párrafo si viene vacío.
                precio: '',
                // Legaliboo no informa descuento; el de onLygal es el mismo que anuncia el banner.
                descuento: '-20%',
                // El icono se resuelve por producto, no por servicio (ver iconForCard).
                iconKey: producto.productoDescripcion || '',
                detalleSlug: '',
                categoriaSlug: '',
                externalUrl: environment.serviciosLegalesUrl + '#servicios'
            } as BenefitCard));
    }
}